import React, { ReactNode, ReactElement, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material";
import {
  ComponentsOverrides as AbsoluteSightComponentsOverrides,
  ThemeOptions as AbsoluteSightThemeOptions,
} from "./absolute-sight";
import {
  ComponentsOverrides as EnergiTeamComponentsOverrides,
  ThemeOptions as EnergiTeamThemeOptions,
} from "./energiteam";
import {
  ComponentsOverrides as SustainorComponentsOverrides,
  ThemeOptions as SustainorThemeOptions,
} from "./sustainor";
import {
  ComponentsOverrides as NordicComponentsOverrides,
  ThemeOptions as NordicThemeOptions,
} from "./nordic";
import {
  ComponentsOverrides as M4eComponentsOverrides,
  ThemeOptions as M4eThemeOptions,
} from "./m4e";
import {
  ComponentsOverrides as FabekeComponentsOverrides,
  ThemeOptions as FabekeThemeOptions,
} from "./fabeke";
import { useLang } from "./../";
// import { daDK as dataGridDaDK, deDE as dataGridDeDE, enUS as dataGridEnUS } from "@mui/x-data-grid";
// import { daDK as dataGridDaDK, deDE as dataGridDeDE, enUS as dataGridEnUS } from "@mui/material/locale";
import {
  daDK as coreDaDK,
  deDE as coreDeDE,
  enUS as coreEnUS,
  esES as coreEsES,
  frFR as coreFrFR,
  nbNO as coreNbNO,
  nlNL as coreNlNL,
  svSE as coreSvSE,
} from "@mui/material/locale";
import { getPartnerKey } from "@/utils";

/**
 * MaterialThemeProvider component to manage theme and localization.
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The children components.
 * @returns {ReactElement} - The MaterialThemeProvider component.
 */
interface MaterialThemeProviderProps {
  children: ReactNode;
}

export const MaterialThemeProvider = (
  props: MaterialThemeProviderProps,
): ReactElement => {
  const partnerKey = getPartnerKey();
  const { children } = props;
  const locale = useLang();

  // let themeOptionsDefault = defaultThemeOptions();
  // let ComponentsOverrides = defaultComponentsOverrides;

  let themeOptionsDefault = FabekeThemeOptions();
  let ComponentsOverrides = FabekeComponentsOverrides;

  switch (partnerKey) {
    case "energiteam":
      themeOptionsDefault = EnergiTeamThemeOptions();
      ComponentsOverrides = EnergiTeamComponentsOverrides;
      break;
    case "sustainor":
      themeOptionsDefault = SustainorThemeOptions();
      ComponentsOverrides = SustainorComponentsOverrides;
      break;
    case "nordic":
      themeOptionsDefault = NordicThemeOptions();
      ComponentsOverrides = NordicComponentsOverrides;
      break;
    case "m4e":
      themeOptionsDefault = M4eThemeOptions();
      ComponentsOverrides = M4eComponentsOverrides;
      break;
    case "fabeke":
      themeOptionsDefault = FabekeThemeOptions();
      ComponentsOverrides = FabekeComponentsOverrides;
      break;
    case "absolute-sight":
      themeOptionsDefault = AbsoluteSightThemeOptions();
      ComponentsOverrides = AbsoluteSightComponentsOverrides;
      break;
    case "default":
    default:
      themeOptionsDefault = FabekeThemeOptions();
      // themeOptionsDefault = defaultThemeOptions();
      // ComponentsOverrides = defaultComponentsOverrides;
      break;
  }

  let selectedLanguage = coreEnUS;
  switch (locale) {
    case "da":
      selectedLanguage = coreDaDK;
      break;
    case "de":
      selectedLanguage = coreDeDE;
      break;
    case "es":
      selectedLanguage = coreEsES;
      break;
    case "fr":
      selectedLanguage = coreFrFR;
      break;
    case "nb":
      selectedLanguage = coreNbNO;
      break;
    case "nl":
      selectedLanguage = coreNlNL;
      break;
    case "no":
      selectedLanguage = coreNbNO;
      break;
    case "sv":
      selectedLanguage = coreSvSE;
      break;
    default:
      break;
  }

  const themeOptions = useMemo(
    () => themeOptionsDefault,
    [themeOptionsDefault],
  );

  const theme = createTheme(themeOptions, selectedLanguage);

  theme.components = ComponentsOverrides(theme);

  return <ThemeProvider theme={theme}> {children}</ThemeProvider>;
};
